import { GetStaticProps } from 'next';

import { KnowledgeCentreWithSearch } from '../../components/KnowledgeArticle/KnowledgeCentreWithSearch';
import {
  KnowledgeCategoryView,
  KnowledgeHelpIndex,
} from '../../components/KnowledgeArticle/KnowledgeHelpIndex';
import {
  getKnowledgeArticleBySlug,
  getKnowledgeArticles,
  getKnowledgeCategories,
} from '../../utils/knowledge/knowledgeArticles';
import {
  getKnowledgeCopy,
  isKnowledgeLocale,
  KnowledgeLocale,
  resolveKnowledgeLocale,
} from '../../utils/knowledge/knowledgeCopy';
import { KnowledgeCategory, KnowledgeChannel } from '../../utils/knowledge/types';
import { getKnowledgeMinimalPageProps } from '../../utils/knowledge/getKnowledgeMinimalPageProps';
import { NEXT_ISR_REVALIDATE_SECONDS } from '../../utils/isr';
import { ServiceOSGlobalSettings } from '../../utils/getServiceOSGlobalSettings';

const CHANNEL: KnowledgeChannel = 'hero';

interface HeroHelpIndexProps {
  locale: KnowledgeLocale;
  categories: KnowledgeCategoryView[];
  title: string;
  intro: string;
  topArticles: { urlName: string; title: string }[];
  serviceOS: ServiceOSGlobalSettings;
}

const HeroHelpIndex = ({ locale, categories, title, intro, topArticles }: HeroHelpIndexProps) => {
  const allArticles = getKnowledgeArticles(locale, CHANNEL);

  return (
    <KnowledgeCentreWithSearch
      channel={CHANNEL}
      locale={locale}
      title={`${title} | Urban Hero help`}
      description={intro}
      articles={allArticles}
    >
      <KnowledgeHelpIndex
        locale={locale}
        channel={CHANNEL}
        title={title}
        topArticles={topArticles}
        categories={categories}
      />
    </KnowledgeCentreWithSearch>
  );
};

export default HeroHelpIndex;

function buildCategoryViews(locale: string, rawCategories: KnowledgeCategory[]): KnowledgeCategoryView[] {
  return rawCategories
    .map((category) => {
      const articles = category.articleSlugs
        .map((slug) => {
          const article = getKnowledgeArticleBySlug(slug, locale, CHANNEL);
          return article ? { urlName: article.urlName, title: article.title } : null;
        })
        .filter(Boolean) as { urlName: string; title: string }[];

      const seen = new Set<string>();
      const unique = articles.filter((article) => {
        if (seen.has(article.urlName)) {
          return false;
        }
        seen.add(article.urlName);
        return true;
      });

      return { name: category.name, slug: category.slug, articles: unique };
    })
    .filter((category) => category.articles.length > 0);
}

export const getStaticProps: GetStaticProps<HeroHelpIndexProps> = async ({ locale }) => {
  const resolvedLocale = resolveKnowledgeLocale(locale);

  if (!isKnowledgeLocale(resolvedLocale)) {
    return { notFound: true };
  }

  const copy = getKnowledgeCopy(resolvedLocale);
  const categories = buildCategoryViews(resolvedLocale, getKnowledgeCategories(resolvedLocale, CHANNEL));
  const allArticles = getKnowledgeArticles(resolvedLocale, CHANNEL);
  const topArticles = allArticles.slice(0, 5).map(({ urlName, title }) => ({ urlName, title }));

  const minimalProps = await getKnowledgeMinimalPageProps(resolvedLocale);

  return {
    props: {
      locale: resolvedLocale,
      categories,
      title: copy.heroIndexTitle,
      intro: copy.heroIndexIntro,
      topArticles,
      ...minimalProps,
    },
    revalidate: NEXT_ISR_REVALIDATE_SECONDS,
  };
};
